/**
 * Playlist Mapper
 *
 * Maps between legacy types (@/types/MusicPlaylist) and domain entities (@/domain/entities).
 * Keeps domain entities pure from external data shapes (Clean Architecture).
 *
 * Follows:
 * - adapt-mapper-translation: Use mappers to translate between layers
 * - dep-data-crossing-boundaries: Use simple data structures across boundaries
 */

import type {
  Track as DomainTrack,
  TrackMetadata as DomainTrackMetadata,
  StreamingLinks as DomainStreamingLinks,
  Playlist as DomainPlaylist,
} from '@/domain/entities';
import { createTrack, createPlaylist } from '@/domain/entities';
import type {
  Track as LegacyTrack,
  Playlist as LegacyPlaylist,
  StreamingLinks as LegacyStreamingLinks,
} from '@/types/MusicPlaylist';

/**
 * Maps between legacy types and domain entities.
 * Pure functions with no side effects.
 */
export const PlaylistMapper = {
  /**
   * Map legacy streaming links to domain format.
   * Handles field name differences (apple -> appleMusic).
   */
  toDomainStreamingLinks(
    legacy: LegacyStreamingLinks | undefined
  ): DomainStreamingLinks | undefined {
    if (!legacy) return undefined;

    const links: DomainStreamingLinks = {};

    if (legacy.spotify) links.spotify = legacy.spotify;
    if (legacy.apple) links.appleMusic = legacy.apple;
    if (legacy.youtube) links.youtube = legacy.youtube;
    if (legacy.soundcloud) links.soundcloud = legacy.soundcloud;

    // Return undefined if no links present
    return Object.keys(links).length > 0 ? links : undefined;
  },

  /**
   * Map domain streaming links to legacy format.
   * Handles field name differences (appleMusic -> apple).
   */
  toLegacyStreamingLinks(
    domain: DomainStreamingLinks | undefined
  ): LegacyStreamingLinks | undefined {
    if (!domain) return undefined;

    const links: LegacyStreamingLinks = {};

    if (domain.spotify) links.spotify = domain.spotify;
    if (domain.appleMusic) links.apple = domain.appleMusic;
    if (domain.youtube) links.youtube = domain.youtube;
    if (domain.soundcloud) links.soundcloud = domain.soundcloud;

    return Object.keys(links).length > 0 ? links : undefined;
  },

  /**
   * Map legacy Track to domain Track entity.
   */
  toDomainTrack(legacy: LegacyTrack): DomainTrack {
    const metadata: DomainTrackMetadata | undefined = legacy.metadata
      ? {
          album: legacy.metadata.album,
          genre: legacy.metadata.genre,
          year: legacy.metadata.year,
          streamingLinks: PlaylistMapper.toDomainStreamingLinks(
            legacy.metadata.streamingLinks
          ),
        }
      : undefined;

    return createTrack({
      id: legacy.id,
      title: legacy.title,
      artist: legacy.artist,
      audioUrl: legacy.audioUrl,
      coverUrl: legacy.coverUrl,
      duration: legacy.duration,
      metadata,
    });
  },

  /**
   * Map domain Track to legacy format (for backward compatibility).
   */
  toLegacyTrack(track: DomainTrack): LegacyTrack {
    return {
      id: track.id,
      title: track.title,
      artist: track.artist,
      audioUrl: track.audioUrl,
      coverUrl: track.coverUrl,
      duration: track.duration,
      metadata: track.metadata
        ? {
            album: track.metadata.album,
            genre: track.metadata.genre,
            year: track.metadata.year,
            streamingLinks: PlaylistMapper.toLegacyStreamingLinks(
              track.metadata.streamingLinks
            ),
          }
        : undefined,
    };
  },

  /**
   * Map legacy Playlist to domain Playlist entity.
   */
  toDomainPlaylist(legacy: LegacyPlaylist): DomainPlaylist {
    return createPlaylist({
      id: legacy.id,
      name: legacy.name,
      description: legacy.description,
      tracks: legacy.tracks.map((t) => PlaylistMapper.toDomainTrack(t)),
      coverUrl: legacy.coverUrl,
      createdAt: new Date(legacy.createdAt),
      updatedAt: new Date(legacy.updatedAt),
    });
  },

  /**
   * Map domain Playlist to legacy format.
   */
  toLegacyPlaylist(playlist: DomainPlaylist): LegacyPlaylist {
    return {
      id: playlist.id,
      name: playlist.name,
      description: playlist.description,
      tracks: [...playlist.tracks].map((t) => PlaylistMapper.toLegacyTrack(t)),
      coverUrl: playlist.coverUrl,
      createdAt: playlist.createdAt.toISOString(),
      updatedAt: playlist.updatedAt.toISOString(),
    };
  },
};
